import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { PiCheckCircle, PiUsersThree } from 'react-icons/pi';
import BottomSheet from '../BottomSheet';
import { useFeedback } from '../../FeedbackContext';
import { shareTransactionToAccount } from '../../services/transactionSharingService';
import { getAccountDisplayName, getAccountRoleLabel } from '../../utils/accountPresentation';
import './styles.css';

const WRITABLE_ROLES = new Set(['owner', 'admin', 'editor']);

const ShareTransactionSheet = ({ transaction, sourceAccountId, accounts, onClose }) => {
  const { showError, showSuccess } = useFeedback();
  const [sharingId, setSharingId] = useState(null);
  const familyAccounts = useMemo(() => accounts.filter((account) => (
    account.id !== sourceAccountId
      && account.type === 'family'
      && WRITABLE_ROLES.has(account.role)
  )), [accounts, sourceAccountId]);

  const handleShare = async (account) => {
    if (sharingId) return;
    setSharingId(account.id);
    try {
      await shareTransactionToAccount({
        transaction,
        sourceAccountId,
        targetAccountId: account.id,
      });
      showSuccess(`A movimentação foi adicionada em ${getAccountDisplayName(account)}.`, 'Compartilhada com a família');
      onClose();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Falha ao compartilhar movimentação:', {
          stage: 'share-transaction-to-family',
          accountId: account.id,
          code: error?.code || error?.message || 'unknown',
        });
      }
      showError('Não foi possível adicionar esta movimentação à conta familiar.', 'Compartilhamento não concluído');
    } finally {
      setSharingId(null);
    }
  };

  return (
    <BottomSheet
      title="Compartilhar com a família"
      subtitle="A movimentação pessoal será mantida e uma cópia será adicionada ao espaço familiar."
      onClose={onClose}
      size="sm"
      className="share-transaction-sheet"
    >
      {familyAccounts.length === 0 ? (
        <div className="share-transaction-sheet__empty">
          <PiUsersThree aria-hidden="true" />
          <strong>Nenhuma conta familiar disponível</strong>
          <p>Crie ou participe de uma conta familiar com permissão para editar.</p>
        </div>
      ) : (
        <div className="share-transaction-sheet__accounts" role="list">
          {familyAccounts.map((account) => (
            <button
              type="button"
              role="listitem"
              key={account.id}
              disabled={Boolean(sharingId)}
              onClick={() => handleShare(account)}
            >
              <span className="share-transaction-sheet__icon" aria-hidden="true"><PiUsersThree /></span>
              <span><strong>{getAccountDisplayName(account)}</strong><small>Conta familiar · {getAccountRoleLabel(account.role)}</small></span>
              {sharingId === account.id && <span className="share-transaction-sheet__status">Adicionando...</span>}
              {!sharingId && <PiCheckCircle className="share-transaction-sheet__action" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </BottomSheet>
  );
};

ShareTransactionSheet.propTypes = {
  transaction: PropTypes.object.isRequired,
  sourceAccountId: PropTypes.string.isRequired,
  accounts: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ShareTransactionSheet;
